import React from 'react';
import { Helmet } from 'react-helmet';

const SEOHead = ({ 
  title = 'TourPilot.biz - Tour Operator Software & Package Management',
  description = 'Professional B2B tour operator software for package management, booking systems, and financial tracking. Best DMC software for travel agencies.',
  keywords = '',
  canonicalUrl = 'https://tourpilot.biz',
  ogImage = 'https://tourpilot.biz/og-image.png',
  lang = 'en'
}) => {
  // Comprehensive tour operator keywords for SEO
  const defaultKeywords = [
    // Tier 1 - High Commercial Value
    'tour operator software',
    'B2B travel portal',
    'DMC software',
    'travel back office system',
    'online booking engine for tour operators',
    'B2B hotel booking engine',
    'wholesale travel suppliers',
    'white label travel website',
    
    // Tier 2 - Automation & SaaS
    'automated tour itinerary builder',
    'travel agency management software',
    'tour package management system',
    'travel booking software',
    'tourism management software',
    'travel CRM software',
    'tour operator CRM',
    
    // Tier 3 - Operations
    'travel API integration',
    'GDS flight booking system',
    'travel distribution channels',
    'OTA integration for tour operators',
    'travel consolidators',
    
    // Long-tail AI Search Keywords
    'best mid-office software for tour operators',
    'travel agency booking system',
    'tour package costing software',
    'DMC management software',
    'inbound tour operator software',
    'destination management company software',
    'travel supplier management',
    'tour operator accounting software',
    
    // Regional/Destination Keywords
    'DMC software Europe',
    'tour operator software Finland',
    'Nordic travel agency software',
    'luxury tour operator software',
    
    // Feature Keywords
    'tour package profit tracking',
    'travel expense management',
    'tour booking reconciliation',
    'supplier invoice management travel',
    'tour operator financial software',
    
    // Problem/Solution Keywords
    'how to manage tour bookings',
    'tour package cost control',
    'travel agency automation',
    'tour operator efficiency tools'
  ].join(', ');

  const allKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  // Schema.org structured data for SaaS product
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "TourPilot.biz",
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "Travel Management Software",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "description": "Free trial available"
    },
    "description": "Professional tour operator software for package management, booking tracking, and financial oversight. Designed for DMCs and travel agencies.",
    "featureList": [
      "Tour Package Management",
      "Booking Cost Tracking",
      "Supplier Invoice Management",
      "Financial Dashboard",
      "Profit & Contingency Monitoring",
      "Multi-currency Support",
      "Bottom-up Cost Planning",
      "Document Management"
    ],
    "screenshot": "https://tourpilot.biz/screenshot.png",
    "softwareVersion": "1.0",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "50"
    },
    "author": {
      "@type": "Organization",
      "name": "TourPilot",
      "url": "https://tourpilot.biz"
    }
  };

  // Organization schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "TourPilot.biz",
    "url": "https://tourpilot.biz",
    "logo": "https://tourpilot.biz/logo.png",
    "description": "B2B tour operator software and DMC management solutions",
    "foundingDate": "2024",
    "sameAs": [
      "https://linkedin.com/company/tourpilot",
      "https://twitter.com/tourpilotbiz"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "info@tourpilot.biz"
    }
  };

  // FAQ Schema for AI search engines
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the best tour operator software for small businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "TourPilot.biz offers professional tour package management with a free trial, making it ideal for small to medium tour operators and DMCs looking for affordable, comprehensive software."
        }
      },
      {
        "@type": "Question",
        "name": "How does TourPilot help with tour package costing?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "TourPilot provides bottom-up cost planning, allowing you to build packages from individual cost elements like accommodation, transportation, and guides. Track estimated vs actual costs in real-time."
        }
      },
      {
        "@type": "Question",
        "name": "Can TourPilot track profit and contingency for tour packages?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, TourPilot includes comprehensive profit and contingency monitoring. Track planned vs actual profit, contingency usage, and overall margins for each tour package."
        }
      },
      {
        "@type": "Question",
        "name": "Is TourPilot suitable for Destination Management Companies (DMCs)?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "TourPilot is specifically designed for DMCs and inbound tour operators. It handles supplier management, cost tracking, document storage, and financial reporting needed for DMC operations."
        }
      }
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="author" content="TourPilot.biz" />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="TourPilot.biz" />
      <meta property="og:locale" content={lang} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#2563eb" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="TourPilot" />
      
      {/* Geo tags for regional SEO */}
      <meta name="geo.region" content="FI" />
      <meta name="geo.placename" content="Helsinki" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
